if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.navButtons = {
          prev: this.querySelector('.product__media-nav--prev'),
          next: this.querySelector('.product__media-nav--next'),
        };
        if (this.navButtons.prev) this.navButtons.prev.addEventListener('click', this.navigate.bind(this, -1));
        if (this.navButtons.next) this.navButtons.next.addEventListener('click', this.navigate.bind(this, 1));
        this.updateNavButtons();
        this.updateNavInsets();
        window.addEventListener('resize', debounce(this.updateNavInsets.bind(this), 100));
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      navigate(direction) {
        const slides = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (slides.length < 2) return;
        let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (currentIndex === -1) currentIndex = 0;
        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= slides.length) return;
        this.setActiveMedia(slides[nextIndex].dataset.mediaId, false);
      }

      updateNavButtons() {
        if (!this.navButtons || (!this.navButtons.prev && !this.navButtons.next)) return;
        const slides = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (slides.length < 2) {
          if (this.navButtons.prev) this.navButtons.prev.setAttribute('disabled', 'disabled');
          if (this.navButtons.next) this.navButtons.next.setAttribute('disabled', 'disabled');
          return;
        }
        let currentIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        if (currentIndex === -1) currentIndex = 0;
        if (this.navButtons.prev) this.navButtons.prev.toggleAttribute('disabled', currentIndex === 0);
        if (this.navButtons.next) this.navButtons.next.toggleAttribute('disabled', currentIndex === slides.length - 1);
      }

      updateNavInsets() {
        if (!this.navButtons || (!this.navButtons.prev && !this.navButtons.next)) return;
        const activeItem = this.elements.viewer.querySelector('.product__media-item.is-active');
        const imageContainer = activeItem && activeItem.querySelector('.product-media-container');
        if (!imageContainer) return;
        const viewerRect = this.elements.viewer.getBoundingClientRect();
        const imageRect = imageContainer.getBoundingClientRect();
        if (viewerRect.width === 0 || imageRect.width === 0) return;
        const leftInset = Math.max(0, imageRect.left - viewerRect.left);
        const rightInset = Math.max(0, viewerRect.right - imageRect.right);
        this.style.setProperty('--gallery-image-inset-left', `${leftInset}px`);
        this.style.setProperty('--gallery-image-inset-right', `${rightInset}px`);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateNavButtons();
        this.updateNavInsets();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
